import { ChevronRightIcon } from "@chakra-ui/icons";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { AllocationDetailsFormFields } from "features/allocations/AllocationDetailsFormFields";
import { useAllocationRuleCreate } from "gen/endpoints/allocation-rule/allocation-rule";
import { AllocationRuleCreateDataRequest } from "gen/model/allocationRuleCreateDataRequest";
import { AllocationRuleReadData } from "gen/model/allocationRuleReadData";
import { allocationRuleCreateBody } from "gen/schemas/allocation-rule/allocation-rule";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { resolvePath, useNavigate, useParams } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
  FormFooterBreadcrumb,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { useRouteBasePath } from "shared/navigation/UseRouteBasePath";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { z } from "zod";

const schema = z.intersection(
  allocationRuleCreateBody,
  z.object({
    transaction_calculation_id: z.string().min(1),
  }),
);

export const AllocationCreatePage: React.FC = () => {
  const { t } = useTranslation(["allocation", "common"]);
  const navigate = useNavigate();
  const { fundFamilyId } = useRequiredStringParams();
  const { entityId } = useParams();
  const basePath = useRouteBasePath();

  const form = useForm<AllocationRuleCreateDataRequest>({
    resolver: zodResolver(schema),
    defaultValues: {
      fund_family_id: fundFamilyId,
      entity_id: entityId,
      effective_start_date: nowAsIsoDate(),
    },
  });

  const { mutateAsync: createAllocation } = useAllocationRuleCreate();

  return (
    <PageContent>
      <Form
        form={form}
        onSubmit={(data) => createAllocation({ fundFamilyId, data })}
        onSuccess={(response: AllocationRuleReadData) => {
          navigate(
            // navigate from "allocations/create" to "allocations/setup/:allocationRuleId/entities"
            `../setup/${response.id}/entities`,
            {
              replace: true,
            },
          );
        }}
      >
        <PageHeader
          size="lg"
          workflowName={t("allocation:setup.superHeading")}
          title={t("allocation:setup.details.title")}
        />
        <PageBody>
          <ContentBlockGroup>
            <ContentBlock>
              <ContentBlockBody variant="form">
                <AllocationDetailsFormFields
                  fundFamilyId={fundFamilyId}
                  entityId={entityId}
                />
              </ContentBlockBody>
            </ContentBlock>
          </ContentBlockGroup>
        </PageBody>
        <FormFooter>
          <FormFooterBreadcrumb>
            <Breadcrumb
              separator={<ChevronRightIcon />}
              data-ls-enabled={true}
              data-public={true}
            >
              {[
                t("allocation:setup.details.title"),
                t("allocation:setup.entities.title"),
              ].map((title, index) => (
                <BreadcrumbItem key={index} isCurrentPage={index === 0}>
                  <BreadcrumbLink aria-disabled={true}>{title}</BreadcrumbLink>
                </BreadcrumbItem>
              ))}
            </Breadcrumb>
          </FormFooterBreadcrumb>
          <FormFooterActions>
            <FormCancelButton
              onClick={() => {
                // navigate from "allocations/create" to "allocations"
                navigate(resolvePath("..", basePath).pathname);
              }}
            />
            <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
          </FormFooterActions>
        </FormFooter>
      </Form>
    </PageContent>
  );
};
